/**
 * Dynamic Product Status System
 * Handles real-time status updates based on inventory levels and quantity changes
 */

class DynamicProductStatus {
  constructor() {
    this.statusElements = new Map();
    this.init();
  }

  init() {
    // Find all dynamic status elements
    this.findStatusElements();
    
    // Listen for quantity changes
    this.setupQuantityListeners();
    
    // Listen for variant changes
    this.setupVariantListeners();
    
    // Initial status update
    this.updateAllStatuses();
  }

  findStatusElements() {
    const elements = document.querySelectorAll('.dynamic-status');
    elements.forEach(element => {
      const variantId = element.dataset.variantId;
      if (variantId) {
        this.statusElements.set(variantId, element);
      }
    });
  }

  setupQuantityListeners() {
    // Listen for quantity input changes (both product page and cart)
    document.addEventListener('change', (event) => {
      if (event.target.matches('input[name="quantity"]') || event.target.matches('input[name="updates[]"]')) {
        this.handleQuantityChange(event.target);
      }
    });

    // Listen for quantity button clicks
    document.addEventListener('click', (event) => {
      if (event.target.matches('.quantity__button')) {
        setTimeout(() => {
          const quantityContainer = event.target.closest('.quantity');
          if (quantityContainer) {
            // Try both product page and cart drawer quantity input names
            const quantityInput = quantityContainer.querySelector('input[name="quantity"]') || 
                                  quantityContainer.querySelector('input[name="updates[]"]');
            if (quantityInput) {
              this.handleQuantityChange(quantityInput);
            }
          }
        }, 100);
      }
    });
  }

  setupVariantListeners() {
    // Listen for variant changes (if using variant selector)
    document.addEventListener('change', (event) => {
      if (event.target.matches('input[name="id"]')) {
        this.handleVariantChange(event.target.value);
      }
    });
  }

  handleQuantityChange(quantityInput) {
    const quantity = parseInt(quantityInput.value) || 1;
    
    // Try to get variant ID from product form (product page)
    const form = quantityInput.closest('form');
    const variantId = form?.querySelector('input[name="id"]')?.value;
    
    // If not found, try to get from cart drawer (using cart item context)
    if (!variantId) {
      const cartItem = quantityInput.closest('.cart-item');
      if (cartItem) {
        // Find the status badge within this cart item
        const statusBadge = cartItem.querySelector('.dynamic-status');
        if (statusBadge && statusBadge.dataset.variantId) {
          const cartVariantId = statusBadge.dataset.variantId;
          this.updateStatusForVariant(cartVariantId, quantity);
          return;
        }
      }
    }
    
    if (variantId) {
      this.updateStatusForVariant(variantId, quantity);
    }
  }

  handleVariantChange(variantId) {
    const quantityInput = document.querySelector('input[name="quantity"]');
    const quantity = quantityInput ? parseInt(quantityInput.value) || 1 : 1;
    this.updateStatusForVariant(variantId, quantity);
  }

  updateStatusForVariant(variantId, quantity) {
    const statusElement = this.statusElements.get(variantId);
    if (!statusElement) return;

    const currentInventory = parseInt(statusElement.dataset.inventory) || 0;
    const baseStatus = statusElement.dataset.baseStatus || '';
    
    // Add updating class for visual feedback
    statusElement.classList.add('updating');
    
    // Calculate new status
    const newStatus = this.calculateStatus(baseStatus, currentInventory, quantity);
    
    // Update the element
    this.updateStatusElement(statusElement, newStatus, quantity);
    
    // Remove updating class
    setTimeout(() => {
      statusElement.classList.remove('updating');
    }, 300);
  }

  calculateStatus(baseStatus, inventory, quantity) {
    // Debug logging
    console.log('Dynamic Status Calculation:', {
      baseStatus,
      inventory,
      quantity,
      inventoryType: typeof inventory,
      quantityType: typeof quantity
    });

    // HIGHEST PRIORITY: If baseStatus (metafield) is set, respect it to match filters
    if (baseStatus && baseStatus.trim() !== '') {
      const baseStatusLower = baseStatus.toLowerCase().trim();
      if (baseStatusLower === 'in stock') {
        return {
          status: 'In Stock',
          class: 'product-status-icon--in-stock',
          message: 'in stock | ships within 2–3 business days',
          isMixed: false
        };
      } else if (baseStatusLower === 'made to order') {
        return {
          status: 'Made to Order',
          class: 'product-status-icon--made-to-order',
          message: 'made to order | ships within 8–12 weeks',
          isMixed: false
        };
      } else if (baseStatusLower === 'in transit') {
        return {
          status: 'In Transit',
          class: 'product-status-icon--in-transit',
          message: 'in transit | 4-6 week lead time',
          isMixed: false
        };
      }
    }
    
    if (quantity <= inventory) {
      return {
        status: 'In Stock',
        class: 'product-status-icon--in-stock',
        message: 'in stock | ships within 2–3 business days',
        isMixed: false
      };
    } else if (inventory > 0) {
      // Check if mixed status is enabled (default to true)
      const showMixedStatus = true; // You can make this configurable
      if (showMixedStatus) {
        return {
          status: 'Mixed Stock',
          class: 'product-status-icon--mixed-stock',
          message: `partial stock | ${inventory} boxes in stock, ${quantity - inventory} boxes in transit`,
          isMixed: true
        };
      } else {
        return {
          status: 'In Stock',
          class: 'product-status-icon--in-stock',
          message: 'in stock | ships within 2–3 business days',
          isMixed: false
        };
      }
    } else {
      // No inventory or negative inventory - Check base status, especially for Made to Order
      const baseStatusLower = baseStatus ? baseStatus.toLowerCase() : '';
      if (baseStatusLower.includes('made to order') || baseStatusLower.includes('made-to-order')) {
        return {
          status: 'Made to Order',
          class: 'product-status-icon--made-to-order',
          message: 'made to order | ships within 8–12 weeks',
          isMixed: false
        };
      } else {
        // No inventory - show In Transit if not Made to Order
        return {
          status: 'In Transit',
          class: 'product-status-icon--in-transit',
          message: 'in transit | 4-6 week lead time',
          isMixed: false
        };
      }
    }
  }

  updateStatusElement(element, newStatus, quantity) {
    const icon = element.querySelector('.product-status-icon');
    const text = element.querySelector('.product-status-text');
    
    if (icon && text) {
      // Update icon class
      icon.className = `product-status-icon ${newStatus.class}`;
      
      // Update icon SVG
      const svg = icon.querySelector('svg');
      if (svg) {
        if (newStatus.isMixed) {
          svg.innerHTML = `
            <rect x="2" y="20" width="40" height="24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
            <rect x="42" y="28" width="18" height="16" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
            <circle cx="18" cy="48" r="5" fill="none" stroke="currentColor" stroke-width="3"/>
            <circle cx="46" cy="48" r="5" fill="none" stroke="currentColor" stroke-width="3"/>
            <rect x="8" y="8" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          `;
        } else {
          svg.innerHTML = `
            <rect x="2" y="20" width="40" height="24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
            <rect x="42" y="28" width="18" height="16" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
            <circle cx="18" cy="48" r="5" fill="none" stroke="currentColor" stroke-width="3"/>
            <circle cx="46" cy="48" r="5" fill="none" stroke="currentColor" stroke-width="3"/>
          `;
        }
      }
      
      // Update text
      text.innerHTML = `<strong>${newStatus.status.toLowerCase()}</strong> | ${newStatus.message}`;
    }
    
    // Update data attributes
    element.dataset.quantity = quantity;
  }

  updateAllStatuses() {
    this.statusElements.forEach((element, variantId) => {
      const quantity = parseInt(element.dataset.quantity) || 1;
      this.updateStatusForVariant(variantId, quantity);
    });
  }

  // Public method to refresh status for a specific variant
  refreshVariantStatus(variantId, inventory, baseStatus) {
    const element = this.statusElements.get(variantId);
    if (element) {
      element.dataset.inventory = inventory;
      element.dataset.baseStatus = baseStatus;
      const quantity = parseInt(element.dataset.quantity) || 1;
      this.updateStatusForVariant(variantId, quantity);
    }
  }
}

// Initialize when DOM is ready
let dynamicProductStatusInstance = null;

function initDynamicProductStatus() {
  if (dynamicProductStatusInstance) {
    // Re-initialize to find new elements after cart updates
    dynamicProductStatusInstance.findStatusElements();
    dynamicProductStatusInstance.updateAllStatuses();
  } else {
    dynamicProductStatusInstance = new DynamicProductStatus();
  }
}

document.addEventListener('DOMContentLoaded', () => {
  // Initialize the dynamic product status system
  initDynamicProductStatus();
  
  // Set up MutationObserver to watch for cart drawer updates
  const cartDrawer = document.querySelector('cart-drawer');
  if (cartDrawer) {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.addedNodes.length > 0) {
          // Check if any new dynamic-status elements were added
          const hasNewStatusBadges = Array.from(mutation.addedNodes).some(node => {
            if (node.nodeType === 1) { // Element node
              return node.querySelector?.('.dynamic-status') || node.classList?.contains('dynamic-status');
            }
            return false;
          });
          
          if (hasNewStatusBadges && dynamicProductStatusInstance) {
            // Small delay to ensure DOM is fully updated
            setTimeout(() => {
              dynamicProductStatusInstance.findStatusElements();
              dynamicProductStatusInstance.updateAllStatuses();
            }, 100);
          }
        }
      });
    });
    
    const cartDrawerBody = cartDrawer.querySelector('#CartDrawer-Body');
    if (cartDrawerBody) {
      observer.observe(cartDrawerBody, {
        childList: true,
        subtree: true
      });
    }
  }
});

// Export for use in other scripts
window.DynamicProductStatus = DynamicProductStatus;
window.initDynamicProductStatus = initDynamicProductStatus;
